import type { AxiosInstance } from "axios";
import type {
  ApiResponse,
  PtAvailabilityExceptionDTO,
  PtAvailabilityExceptionUpsertRequest,
  PtAvailabilityTemplateDTO,
  PtAvailabilityTemplateUpsertRequest,
} from "@membentry/shared-types";
import * as api from "@membentry/shared-api";

import { invalidatePtTrainer } from "./invalidation";
import type {
  AppointmentVariables,
  ClubVariables,
  MutationOptions,
} from "./types";
import { usePtMutation } from "./use-pt-mutation";

type TemplateBodyVariables = ClubVariables & {
  body: PtAvailabilityTemplateUpsertRequest;
};
type TemplateUpdateVariables = AppointmentVariables & {
  body: PtAvailabilityTemplateUpsertRequest;
};
type ExceptionBodyVariables = ClubVariables & {
  body: PtAvailabilityExceptionUpsertRequest;
};
type ExceptionUpdateVariables = AppointmentVariables & {
  body: PtAvailabilityExceptionUpsertRequest;
};

export const useCreatePtAvailabilityMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAvailabilityTemplateDTO, TemplateBodyVariables>,
) =>
  usePtMutation(
    client,
    options,
    (apiClient, variables) =>
      api.createPtAvailability(apiClient, variables.clubId, variables.body),
    invalidatePtTrainer,
  );

export const useUpdatePtAvailabilityMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAvailabilityTemplateDTO, TemplateUpdateVariables>,
) =>
  usePtMutation(
    client,
    options,
    (apiClient, variables) =>
      api.updatePtAvailability(
        apiClient,
        variables.clubId,
        variables.id,
        variables.body,
      ),
    invalidatePtTrainer,
  );

export const useDeletePtAvailabilityMutation = (
  client: AxiosInstance,
  options?: MutationOptions<{ id: number }, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    async (apiClient, variables): Promise<ApiResponse<{ id: number }>> =>
      api.deletePtAvailability(apiClient, variables.clubId, variables.id),
    invalidatePtTrainer,
  );

export const useCreatePtAvailabilityExceptionMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAvailabilityExceptionDTO, ExceptionBodyVariables>,
) =>
  usePtMutation(
    client,
    options,
    (apiClient, variables) =>
      api.createPtAvailabilityException(
        apiClient,
        variables.clubId,
        variables.body,
      ),
    invalidatePtTrainer,
  );

export const useUpdatePtAvailabilityExceptionMutation = (
  client: AxiosInstance,
  options?: MutationOptions<PtAvailabilityExceptionDTO, ExceptionUpdateVariables>,
) =>
  usePtMutation(
    client,
    options,
    (apiClient, variables) =>
      api.updatePtAvailabilityException(
        apiClient,
        variables.clubId,
        variables.id,
        variables.body,
      ),
    invalidatePtTrainer,
  );

export const useDeletePtAvailabilityExceptionMutation = (
  client: AxiosInstance,
  options?: MutationOptions<{ id: number }, AppointmentVariables>,
) =>
  usePtMutation(
    client,
    options,
    async (apiClient, variables): Promise<ApiResponse<{ id: number }>> =>
      api.deletePtAvailabilityException(
        apiClient,
        variables.clubId,
        variables.id,
      ),
    invalidatePtTrainer,
  );
